import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Calendar,
    Clock,
    Euro,
    LogOut,
    Wallet,
    TrendingUp,
    Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function startOfWeek(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}
function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

function api() {
    const token = localStorage.getItem("portal_token");
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` },
    });
}

const fmtEur = (n) =>
    new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
    }).format(n || 0);

export default function PortalDashboardPage() {
    const nav = useNavigate();
    const [me, setMe] = useState(null);
    const [summary, setSummary] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("week"); // week | all

    const today = useMemo(() => new Date(), []);
    const ws = useMemo(() => startOfWeek(today), [today]);
    const days = useMemo(
        () => Array.from({ length: 7 }, (_, i) => addDays(ws, i)),
        [ws],
    );
    const dayLabels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

    const logout = () => {
        localStorage.removeItem("portal_token");
        localStorage.removeItem("portal_employee");
        nav("/portal-login");
    };

    const load = async () => {
        try {
            const a = api();
            const [meR, sumR, allShR, payR] = await Promise.all([
                a.get("/portal/me"),
                a.get("/portal/summary"),
                a.get("/portal/shifts"),
                a.get("/portal/payments"),
            ]);
            setMe(meR.data);
            setSummary(sumR.data);
            setShifts(allShR.data.items || []);
            setPayments(payR.data.items || []);
        } catch (e) {
            if (e.response?.status === 401) {
                toast.error("Sessione scaduta");
                logout();
            } else {
                toast.error("Errore di caricamento");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem("portal_token")) {
            nav("/portal-login");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sf-muted">
                Caricamento…
            </div>
        );
    }
    if (!me) return null;

    const weekShifts = shifts.filter(
        (s) => s.date >= fmt(ws) && s.date <= fmt(addDays(ws, 6)),
    );

    return (
        <div className="min-h-screen bg-[#fafaf7]" data-testid="portal-dashboard">
            {/* Header */}
            <header className="bg-navy text-white">
                <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
                    <div>
                        <div className="label-eyebrow text-amber-brand mb-1">
                            Portale Dipendenti
                        </div>
                        <div className="font-display text-2xl">
                            Ciao, {me.name}
                        </div>
                        <div className="text-xs opacity-70 mt-0.5">
                            {me.role} · {me.default_zone} ·{" "}
                            {me.company_name || "—"}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={logout}
                        className="text-white hover:bg-white/10"
                        data-testid="portal-logout"
                    >
                        <LogOut size={14} className="mr-2" />
                        Esci
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
                {/* KPIs salary/extra/hourly */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <KpiCard
                        icon={<Briefcase size={16} />}
                        label="Tipo contratto"
                        value={
                            me.employment_type === "employee"
                                ? "Dipendente"
                                : "A chiamata"
                        }
                        sub={me.role}
                    />
                    <KpiCard
                        icon={<Wallet size={16} />}
                        label="Stipendio netto"
                        value={
                            me.employment_type === "employee"
                                ? fmtEur(me.monthly_net)
                                : "—"
                        }
                        sub={
                            me.employment_type === "employee"
                                ? `${me.weekly_hours}h/sett`
                                : "Non in busta"
                        }
                    />
                    <KpiCard
                        icon={<TrendingUp size={16} />}
                        label="Extra busta"
                        value={fmtEur(me.extra_monthly)}
                        sub="Importo netto mensile"
                    />
                    <KpiCard
                        icon={<Clock size={16} />}
                        label="Tariffa oraria"
                        value={fmtEur(me.hourly_rate)}
                        sub={`per ora${me.employment_type === "employee" ? " (extra)" : ""}`}
                    />
                </div>

                {/* Dovuto vs Pagato */}
                {summary && (
                    <Card className="border-sf">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-display text-xl text-navy">
                                    Dovuto vs Pagato
                                </h2>
                                <Badge variant="outline" className="border-sf">
                                    {summary.month}
                                </Badge>
                            </div>
                            <div className="overflow-x-auto">
                                <table
                                    className="w-full text-sm"
                                    data-testid="portal-owed-table"
                                >
                                    <thead className="bg-[#fafaf7]">
                                        <tr>
                                            <th className="px-3 py-2 text-left label-eyebrow">
                                                Voce
                                            </th>
                                            <th className="px-3 py-2 text-right label-eyebrow">
                                                Dovuto
                                            </th>
                                            <th className="px-3 py-2 text-right label-eyebrow">
                                                Pagato
                                            </th>
                                            <th className="px-3 py-2 text-right label-eyebrow">
                                                Restante
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <Row
                                            label="Stipendio"
                                            o={summary.owed.salary}
                                            p={summary.paid.salary}
                                            r={summary.remaining.salary}
                                        />
                                        <Row
                                            label="Extra busta"
                                            o={summary.owed.extra}
                                            p={summary.paid.extra}
                                            r={summary.remaining.extra}
                                        />
                                        <Row
                                            label="Ore lavorate"
                                            o={summary.owed.hourly}
                                            p={summary.paid.hourly}
                                            r={summary.remaining.hourly}
                                            extra={`${summary.hours} h`}
                                        />
                                        <tr className="border-t-2 border-navy font-bold">
                                            <td className="px-3 py-2.5">
                                                TOTALE
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                {fmtEur(summary.owed.total)}
                                            </td>
                                            <td className="px-3 py-2.5 text-right text-green-700">
                                                {fmtEur(summary.paid.total)}
                                            </td>
                                            <td
                                                className={`px-3 py-2.5 text-right ${summary.remaining.total > 0 ? "text-amber-brand" : "text-green-700"}`}
                                                data-testid="portal-remaining-total"
                                            >
                                                {fmtEur(summary.remaining.total)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tab switcher */}
                <div className="inline-flex bg-white border border-sf rounded-md p-1">
                    <button
                        onClick={() => setTab("week")}
                        className={`px-4 py-1.5 text-sm rounded ${tab === "week" ? "bg-navy text-white" : "text-sf-secondary"}`}
                        data-testid="portal-tab-week"
                    >
                        Settimana corrente
                    </button>
                    <button
                        onClick={() => setTab("all")}
                        className={`px-4 py-1.5 text-sm rounded ${tab === "all" ? "bg-navy text-white" : "text-sf-secondary"}`}
                        data-testid="portal-tab-all"
                    >
                        Tutti i turni
                    </button>
                    <button
                        onClick={() => setTab("pay")}
                        className={`px-4 py-1.5 text-sm rounded ${tab === "pay" ? "bg-navy text-white" : "text-sf-secondary"}`}
                        data-testid="portal-tab-pay"
                    >
                        Storico pagamenti
                    </button>
                </div>

                {/* Shifts week view */}
                {tab === "week" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
                        {days.map((d, i) => {
                            const dayK = fmt(d);
                            const dayShifts = weekShifts.filter(
                                (s) => s.date === dayK,
                            );
                            return (
                                <div
                                    key={dayK}
                                    className="bg-white border border-sf rounded-md p-2 min-h-[140px]"
                                >
                                    <div className="text-xs label-eyebrow text-sf-muted mb-1">
                                        {dayLabels[i]}{" "}
                                        <span className="opacity-50">
                                            {d.getDate()}/{d.getMonth() + 1}
                                        </span>
                                    </div>
                                    {dayShifts.length === 0 ? (
                                        <div className="text-xs text-sf-muted italic text-center mt-4">
                                            Riposo
                                        </div>
                                    ) : (
                                        dayShifts.map((s) => (
                                            <div
                                                key={s.id}
                                                className="bg-navy/5 border border-navy/20 rounded p-1.5 text-xs mb-1.5"
                                            >
                                                <Calendar
                                                    size={10}
                                                    className="inline mr-1"
                                                />
                                                <span className="font-mono">
                                                    {s.start_time}-{s.end_time}
                                                </span>
                                                <div className="text-sf-muted text-[10px] mt-0.5">
                                                    {s.location}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* All shifts list */}
                {tab === "all" && (
                    <div className="bg-white border border-sf rounded-md overflow-hidden">
                        <table className="w-full text-sm" data-testid="portal-shifts-table">
                            <thead className="bg-[#fafaf7]">
                                <tr>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Data
                                    </th>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Orario
                                    </th>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Postazione
                                    </th>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Ruolo
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {shifts.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="text-center py-8 text-sf-muted"
                                        >
                                            Nessun turno
                                        </td>
                                    </tr>
                                )}
                                {shifts.map((s) => (
                                    <tr
                                        key={s.id}
                                        className="border-t border-sf"
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            {s.date}
                                        </td>
                                        <td className="px-3 py-2 font-mono">
                                            {s.start_time} - {s.end_time}
                                        </td>
                                        <td className="px-3 py-2">
                                            {s.location}
                                        </td>
                                        <td className="px-3 py-2">
                                            {s.role || me.role}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Payments history */}
                {tab === "pay" && (
                    <div className="bg-white border border-sf rounded-md overflow-hidden">
                        <table className="w-full text-sm" data-testid="portal-payments-table">
                            <thead className="bg-[#fafaf7]">
                                <tr>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Data
                                    </th>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Categoria
                                    </th>
                                    <th className="px-3 py-2 text-right label-eyebrow">
                                        Importo
                                    </th>
                                    <th className="px-3 py-2 text-left label-eyebrow">
                                        Note
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="text-center py-8 text-sf-muted"
                                        >
                                            Nessun pagamento registrato
                                        </td>
                                    </tr>
                                )}
                                {payments.map((p) => (
                                    <tr
                                        key={p.id}
                                        className="border-t border-sf"
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            {p.date}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge
                                                variant="outline"
                                                className="border-amber-brand text-amber-brand"
                                            >
                                                {p.category === "stipendio"
                                                    ? "Stipendio"
                                                    : p.category ===
                                                        "extra_busta"
                                                      ? "Extra busta"
                                                      : "Ore lavorate"}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono font-bold text-green-700">
                                            {fmtEur(p.amount)}
                                        </td>
                                        <td className="px-3 py-2 text-sf-muted text-xs">
                                            {p.notes || ""}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}

function KpiCard({ icon, label, value, sub }) {
    return (
        <div className="bg-white border border-sf rounded-xl p-3">
            <div className="flex items-center gap-2 text-xs text-sf-muted uppercase tracking-wider mb-1">
                {icon}
                {label}
            </div>
            <div className="font-display text-xl text-navy">{value}</div>
            {sub && <div className="text-xs text-sf-muted mt-0.5">{sub}</div>}
        </div>
    );
}

function Row({ label, o, p, r, extra }) {
    return (
        <tr className="border-t border-sf">
            <td className="px-3 py-2">
                {label}
                {extra && (
                    <span className="text-xs text-sf-muted ml-2">({extra})</span>
                )}
            </td>
            <td className="px-3 py-2 text-right font-mono">{fmtEur(o)}</td>
            <td className="px-3 py-2 text-right font-mono text-green-700">
                {fmtEur(p)}
            </td>
            <td
                className={`px-3 py-2 text-right font-mono ${r > 0 ? "text-amber-brand" : r < 0 ? "text-red-600" : "text-green-700"}`}
            >
                {fmtEur(r)}
            </td>
        </tr>
    );
}
